// eds-iconography glyphs the ported tree needs. The React-19
// `@eventbrite/legacy-eds.iconography` bridge ships a 142-icon subset; the
// glyphs below are NOT in that subset, so they are inlined verbatim from the
// eb-ui source (`packages/eds/eds-iconography/src/icons/`).
//
// Cross-page svg-id audit (React 19 gotcha): every inline svg id/clipPath/mask
// is prefixed with `cc-` (or carries a `useId()`-derived suffix where the
// original used `<use href="#id">`) so two components rendered on one page can
// never collide.
import {
    ArrowLeft,
    ArrowRightChunky,
    CheckChunky,
    CopyChunky,
} from '@eventbrite/legacy-eds.iconography';
import React from 'react';

export { ArrowLeft, ArrowRightChunky, CheckChunky, CopyChunky };

// The original eb-ui CalendarFill used a `<defs><path id/><mask/></defs>` +
// `<use xlinkHref>` pattern. Under React 19 (cross-component id/mask
// collisions on one page) that is unsafe; the mask here was redundant, so it
// is inlined as a single evenodd compound path (the frame + the small date
// squares) — visually identical, no ids/masks to collide.
export const CalendarFill: React.FC<React.SVGProps<SVGSVGElement>> = (
    props,
) => (
    <svg viewBox="0 0 24 24" {...props}>
        <g fill="none" fillRule="evenodd" transform="translate(2 2)">
            <path
                fill="#45494E"
                d="M14 0h1v2h5v4H0V2h5V0h1v2h8V0zM0 20V7h20v13H0zm13-10v2h2v-2h-2zm-4 0v2h2v-2H9zm-4 0v2h2v-2H5zm8 4v2h2v-2h-2zm-4 0v2h2v-2H9zm-4 0v2h2v-2H5z"
            />
        </g>
    </svg>
);
CalendarFill.displayName = 'CalendarFillSvg';

export const LockChunky: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg x={0} y={0} viewBox="0 0 24 24" xmlSpace="preserve" {...props}>
        <path d="M13 15.732V18h-2v-2.268a2 2 0 112 0z" fill="#4B4D63" />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17 8h3v14H4V8h3V7a5 5 0 0110 0v1zm-5-4a3 3 0 013 3v1H9V7a3 3 0 013-3zm6 16V10H6v10h12z"
            fill="#4B4D63"
        />
    </svg>
);
LockChunky.displayName = 'LockChunkySvg';

// The original ScreenLarge used `id="screen-large_svg__..."` on its <svg> +
// inner <path> nodes. React 19 gotcha: duplicate ids/masks across components
// on one page — the ids here were decorative (no <use> refs), so they are
// dropped; the glyph + its default #231F20 fill are unchanged.
export const ScreenLarge: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg x={0} y={0} viewBox="0 0 24 24" xmlSpace="preserve" {...props}>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            fill="#231F20"
            d="M3 18h18v-2H3v2zm0-3h18V8H3v7zm0-8h18V4H3v3zM2 3v16h9.5v2H9v1h6v-1h-2.5v-2H22V3H2z"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            fill="#231F20"
            d="M11 5h2v1h-2z"
        />
    </svg>
);
ScreenLarge.displayName = 'ScreenLargeSvg';

// Same id-dropping as ScreenLarge; the original ScreenSmall relied on the
// (missing) icon-default fill, so it is pinned to the ui-900 black the
// preview buttons expect.
export const ScreenSmall: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg x={0} y={0} viewBox="0 0 24 24" xmlSpace="preserve" {...props}>
        <path
            d="M7 3h10v1H7V3zm0 2h10v12H7V5zm0 13h10v3H7v-3zM6 2v20h12V2H6z"
            fill="#1e0a3c"
        />
        <path d="M11 19h2v1h-2z" fill="#1e0a3c" />
    </svg>
);
ScreenSmall.displayName = 'ScreenSmallSvg';

export const CloudAbstract: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg width={255} height={159} fill="none" {...props}>
        <path
            d="M114.286-24.712C65.106-22.598 11.371-12.518-4.307 13.35c-9.302 15.366 14.637 23.26 1.692 34.115C-22.522 64.172-57 64.666-57 96.173c0 60.266 282.072 78.311 306.272 49.2 28.428-34.257-58.158-43.209-59.069-57.095-.975-13.886 54.645-37.287 50.417-64.425-4.229-27.137-50.677-51.808-126.334-48.565z"
            fill="#fff"
            fillOpacity={0.15}
        />
    </svg>
);
CloudAbstract.displayName = 'CloudAbstractSvg';

/**
 * The sanctioned `@eventbrite/legacy-eds.icon` `Icon` (renders an
 * `<i class="eds-vector-image icon-{size}">` wrapper + the glyph). Imported
 * directly where the component needs it (the legacy Icon's `color=` prop sets
 * a `.color-{token}` class the app's global eds css resolves — e.g.
 * `color="white"` / `"ui-800"`).
 */
export { Icon } from '@eventbrite/legacy-eds.icon';
